import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Volume2 } from 'lucide-react';
import { ItemVisual } from '../components/ItemVisual';
import { audio } from '../core/audio';
import { useActivity } from './useActivity';
import { lang } from '../core/i18n';

export function TapChoice({ activity, onComplete }) {
  const { state, answer, showHint } = useActivity(activity, onComplete);
  const en = lang() === 'en';

  const playPrompt = async () => {
    // Co-play: Piko stays quiet on purpose so the PARENT is the voice. That is
    // the whole point of the mode — a shared moment, not another thing the
    // tablet does for you.
    if (activity.coplay) return;
    if (activity.type === 'sound-match') {
      await audio.playVoice(activity.voice, activity.voiceAlt);
      await audio.playObjectSound(activity.sound);
    } else if (activity.type === 'listen-choose') {
      await audio.playVoice(activity.voice, activity.voiceAlt);
      await audio.playVoice(activity.word);
    } else {
      await audio.playVoice(activity.voice, activity.voiceAlt);
    }
  };

  useEffect(() => { const t = setTimeout(playPrompt, 400); return () => clearTimeout(t); }, [activity.id]); // eslint-disable-line

  const cols = activity.choices.length <= 2 ? 'grid-cols-2' : activity.choices.length === 3 ? 'grid-cols-3' : 'grid-cols-2';

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-8 px-5" data-testid={`activity-${activity.id}`}>
      {activity.coplay && activity.say && (
        <div className="bg-white/90 backdrop-blur rounded-[2rem] px-7 py-4 text-center max-w-sm" style={{ border: '4px solid #fff' }} data-testid="coplay-prompt">
          <p className="font-body text-[11px] font-bold uppercase tracking-wider text-navy/45">
            {en ? 'Say this out loud' : 'Sebut kuat-kuat'}
          </p>
          <p className="font-heading text-4xl font-bold text-navy mt-1">{en ? activity.say.en : activity.say.ms}</p>
          <button data-testid="coplay-hear-btn" onClick={() => audio.playVoice(activity.voice)}
            className="mt-2 inline-flex items-center gap-1.5 font-body text-xs font-bold text-sky min-h-[44px] px-3">
            <Volume2 size={15} strokeWidth={2.8} />
            {en ? 'Hear it' : 'Dengar dulu'}
          </button>
        </div>
      )}
      {activity.target && (
        <div className="flex items-center gap-3 bg-white/75 backdrop-blur rounded-[2rem] px-5 py-3" style={{ border: '4px solid #fff' }} data-testid="detective-target">
          <span className="font-heading text-lg font-bold text-navy/60">🔍</span>
          <ItemVisual item={activity.target} size={64} />
        </div>
      )}
      {(activity.type === 'sound-match' || activity.type === 'listen-choose') && (
        <motion.button whileTap={{ scale: 0.9 }} data-testid="play-prompt-sound-btn"
          onClick={playPrompt}
          className="w-24 h-24 rounded-full bg-white/90 backdrop-blur flex items-center justify-center"
          style={{ boxShadow: 'inset 0 -6px 0 rgba(0,0,0,0.1), 0 10px 24px rgba(29,53,87,0.2)' }}>
          <Volume2 size={44} strokeWidth={3} className="text-sky" />
        </motion.button>
      )}
      <div className={`grid ${cols} gap-5 place-items-center`}>
        {activity.choices.map((ch) => {
          // Some games have no wrong answer at all — tapping anything is the
          // point. They are the safest possible first game in a world, so the
          // child succeeds before they understand the rules.
          const isCorrect = activity.allCorrect || ch.id === activity.correct;
          return (
            <motion.button key={ch.id} data-testid={`choice-${ch.id}`}
              whileTap={{ scale: 0.92 }}
              animate={state === 'correct' && isCorrect ? { scale: [1, 1.18, 1.1] } :
                showHint && isCorrect ? { scale: [1, 1.07, 1] } : {}}
              transition={showHint && isCorrect ? { repeat: Infinity, duration: 1.2 } : { duration: 0.5 }}
              onClick={() => answer(isCorrect)}
              className="rounded-[2rem] p-3 bg-white/70 backdrop-blur-md min-w-[96px] min-h-[96px] flex items-center justify-center"
              style={{
                boxShadow: state === 'correct' && isCorrect
                  ? '0 0 0 6px #FFD166, 0 12px 30px rgba(255,209,102,0.5)'
                  : showHint && isCorrect
                    ? '0 0 0 5px #FFD16690, 0 10px 24px rgba(29,53,87,0.18)'
                    : 'inset 0 -6px 0 rgba(0,0,0,0.08), 0 10px 24px rgba(29,53,87,0.15)',
                border: '5px solid rgba(255,255,255,0.9)',
              }}>
              <ItemVisual item={ch} size={activity.choices.length >= 4 ? 92 : 110} />
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
